import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, FileWarning } from 'lucide-react';
import { cn } from '@/lib/cn';
import { pickProse } from '@/lib/why/prose';
import { formatInstant } from '@/lib/why/format';
import type { NarrativeChapter } from '@/lib/why/types';
import { FactList } from './why-facts';

/**
 * One chapter: the account of a consequence, with the facts it was written from
 * collapsible underneath.
 *
 * `narration: null` IS A FIRST-CLASS STATE, not an error and not a spinner. A
 * chapter that has not been narrated — or whose narration the guards rejected —
 * shows its NAMED reason plus the evidence, because the facts were written
 * first and durably and still answer the question on their own (brief §6.1).
 * What must never appear here is a fabricated sentence standing in for prose
 * that was never grounded.
 */
export function ChapterCard({ chapter }: { chapter: NarrativeChapter }) {
  const { t, i18n } = useTranslation('why');
  const [open, setOpen] = useState(false);
  const lang = i18n.resolvedLanguage ?? i18n.language;

  const headline = pickProse(chapter.narration?.headline, lang);
  const body = pickProse(chapter.narration?.body, lang);
  const cited = new Set(chapter.narration?.factRefs ?? []);
  const factCount = chapter.facts?.length ?? 0;

  return (
    <article
      className="rounded-lg border border-border bg-background p-3"
      data-testid="why-chapter"
      data-chapter-id={chapter.chapterId}
      data-chapter-kind={chapter.kind}
      data-narrated={chapter.narration ? 'true' : 'false'}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="rounded-full bg-muted px-2 py-px text-[10.5px] font-semibold uppercase tracking-wide text-foreground">
          {t(`chapters.kind.${chapter.kind}`, { defaultValue: chapter.kind })}
        </span>
        <time className="text-[11px] text-muted-foreground" dateTime={chapter.occurredAt}>
          {formatInstant(chapter.occurredAt)}
        </time>
      </header>

      {chapter.narration ? (
        <div className="mt-1.5">
          {headline ? (
            <h4
              className="text-[13.5px] font-semibold leading-snug text-foreground"
              data-testid="why-chapter-headline"
            >
              {headline}
            </h4>
          ) : null}
          {body ? (
            <p
              className="mt-0.5 whitespace-pre-line text-xs leading-relaxed text-muted-foreground"
              data-testid="why-chapter-body"
            >
              {body}
            </p>
          ) : null}
        </div>
      ) : (
        <p
          className="mt-1.5 flex items-start gap-1.5 rounded-md bg-muted/60 px-2 py-1.5 text-[11.5px] text-muted-foreground"
          data-testid="why-chapter-no-narration"
          data-narration-error={chapter.narrationError ?? ''}
        >
          <FileWarning className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {chapter.narrationError
              ? t(`chapters.narrationError.${chapter.narrationError}`, {
                  defaultValue: t('chapters.narrationError.unknown'),
                })
              : t(`chapters.narrationState.${chapter.narrationState}`, {
                  defaultValue: t('chapters.narrationState.PENDING'),
                })}
          </span>
        </p>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-2 inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary hover:underline"
        data-testid="why-chapter-facts-toggle"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        {open ? t('chapters.hideFacts') : t('chapters.showFacts', { count: factCount })}
      </button>

      <div className={cn('mt-2', !open && 'hidden')} data-testid="why-chapter-facts-panel">
        {open ? (
          <FactList facts={chapter.facts ?? []} citedIds={cited} testIdPrefix="why-chapter" />
        ) : null}
      </div>
    </article>
  );
}
